import {
  ExpenseDetailsActions,
  ExpenseDetailsMutations,
  ExpenseDetailsGetters
} from './expense-details.keys';
import { expenseDetailsService } from './expense-details.service';
import { attachmentService } from '../../../../components/attachments/attachment.service';
import { map, pick } from 'ramda';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import i18n from '@/i18n';

export const getDefaultState = () => {
  return {
    error: null,
    isLoading: true,
    currentExpense: {
      contractor: {},
      approvers: [],
      assignment: {},
      expenseRates: [],
      expenseEntries: [],
      approverComment: ''
    },
    attachments: [],
    expenseHistories: [],
    isLoadingAttachments: false
  };
};
export const expenseDetailsStore = {
  state: getDefaultState(),
  getters: {
    [ExpenseDetailsGetters.IsLoading]: state => {
      return state.isLoading;
    },
    [ExpenseDetailsGetters.CurrentExpense]: state => {
      return state.currentExpense;
    }
  },
  mutations: {
    [ExpenseDetailsMutations.ResetExpenseState]: function(state) {
      Object.assign(state, getDefaultState());
    },
    [ExpenseDetailsMutations.SetLoading]: function(state) {
      state.isLoading = true;
    },
    [ExpenseDetailsMutations.RetrieveExpenseSuccess]: function(state, expense) {
      state.currentExpense = expense;
      state.isLoading = false;
    },
    [ExpenseDetailsMutations.SetAttachmentsLoading]: function(state) {
      state.isLoadingAttachments = true;
    },
    [ExpenseDetailsMutations.RetrieveAttachmentsSuccess]: function(state, attachments) {
      state.attachments = attachments;
      state.isLoadingAttachments = false;
    },
    [ExpenseDetailsMutations.RetrieveHistorySuccess]: function(state, histories) {
      state.expenseHistories = histories;
    }
  },
  actions: {
    [ExpenseDetailsActions.ResetCurrentExpense]: function({ commit, dispatch }) {
      dispatch(TolErrorsActions.ClearAll);
      commit(ExpenseDetailsMutations.ResetExpenseState);
    },
    [ExpenseDetailsActions.RetrieveExpense]: function({ commit, dispatch }, referenceNumber) {
      commit(ExpenseDetailsMutations.SetLoading);

      const detailsPromise = expenseDetailsService
        .retrieveExpense(referenceNumber)
        .then(expense => {
          commit(ExpenseDetailsMutations.RetrieveExpenseSuccess, expense);
        });
      dispatch(ExpenseDetailsActions.RetrieveExpenseAttachments, referenceNumber);
      return Promise.all([detailsPromise])
        .then(() => {
          dispatch(TolErrorsActions.ClearAll);
          commit(ExpenseDetailsMutations.SetLoading, false);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('expense.errors.retrieveExpense')
          });
          commit(ExpenseDetailsMutations.SetLoading, false);
        });
    },
    [ExpenseDetailsActions.RetrieveExpenseAttachments]: function(
      { commit, dispatch },
      referenceNumber
    ) {
      dispatch(TolErrorsActions.ClearAll);
      commit(ExpenseDetailsMutations.SetAttachmentsLoading);
      return attachmentService
        .getAttachments(referenceNumber, 'EXPENSE_REPORT')
        .then(response => {
          dispatch(TolErrorsActions.ClearAll);
          commit(ExpenseDetailsMutations.RetrieveAttachmentsSuccess, response.data);
          commit(ExpenseDetailsMutations.SetLoading, false);
        })
        .catch(error => {
          // api returns 404 if user is not allowed to see attachments,
          // but an empty array with status 200 if there are no attachments
          if (error.status === 404) return;
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('expenses.details.errors.retrieveAttachments')
          });
          commit(ExpenseDetailsMutations.SetLoading, false);
        });
    },
    [ExpenseDetailsActions.SaveExpense]: function(
      { commit, state, dispatch },
      { action, entries, totalAmount, expenseNote, addEntry }
    ) {
      commit(ExpenseDetailsMutations.SetLoading);

      const prunedEntries = map(
        pick([
          'id',
          'expenseId',
          'entryDate',
          'isUnit',
          'unitQuantity',
          'unitPrice',
          'amount',
          'entryNote'
        ]),
        entries
      );

      return expenseDetailsService
        .saveExpenseEntries(
          state.currentExpense.referenceNumber,
          action,
          prunedEntries,
          totalAmount,
          expenseNote,
          addEntry
        )
        .then(() =>
          dispatch(ExpenseDetailsActions.RetrieveExpense, state.currentExpense.referenceNumber)
        )
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('expense.errors.approverAction')
          });
          commit(ExpenseDetailsMutations.SetLoading, false);
        });
    },
    [ExpenseDetailsActions.ExpenseApproverAction]: function(
      { commit, state, dispatch },
      { action, approverComment }
    ) {
      commit(ExpenseDetailsMutations.SetLoading);

      return expenseDetailsService
        .approveOrRejectExpense(state.currentExpense.referenceNumber, action, approverComment)
        .then(() =>
          dispatch(ExpenseDetailsActions.RetrieveExpense, state.currentExpense.referenceNumber)
        )
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('expense.errors.approverAction')
          });
          commit(ExpenseDetailsMutations.SetLoading, false);
        });
    },
    [ExpenseDetailsActions.ReopenExpense]: function({ commit, state, dispatch }) {
      commit(ExpenseDetailsMutations.SetLoading);

      return expenseDetailsService
        .reopenExpense(state.currentExpense.referenceNumber, ExpenseDetailsActions.ReopenExpense)
        .then(() => {
          dispatch(TolErrorsActions.ClearAll);
          dispatch(ExpenseDetailsActions.RetrieveExpense, state.currentExpense.referenceNumber);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('expense.errors.reopen')
          });
          commit(ExpenseDetailsMutations.SetLoading, false);
        });
    },
    [ExpenseDetailsActions.RetrieveExpenseHistory]: ({ commit, dispatch }, referenceNumber) => {
      return expenseDetailsService
        .retrieveExpenseHistory(referenceNumber)
        .then(expenseHistories => {
          commit(ExpenseDetailsMutations.RetrieveHistorySuccess, expenseHistories);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('expense.errors.retrieveHistory')
          });
        });
    }
  }
};
